if (!customElements.get('push-cart-multibuy')) {
  const PICKER_TRANSITION_MS = 350
  const MOBILE_QUERY = '(max-width: 767px)'

  const emptyElement = (element) => {
    if (!element) {
      return
    }

    if (typeof element.replaceChildren === 'function') {
      element.replaceChildren()
      return
    }

    element.innerHTML = ''
  }

  const submitForm = (form) => {
    if (!form) {
      return
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit()
      return
    }

    const submitButton = form.querySelector(
      'button[type="submit"], input[type="submit"]'
    )

    if (submitButton) {
      submitButton.click()
      return
    }

    form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const bindMediaQuery = (query, handler) => {
    if (!query || typeof handler !== 'function') {
      return () => {}
    }

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', handler)
      return () => query.removeEventListener('change', handler)
    }

    if (typeof query.addListener === 'function') {
      query.addListener(handler)
      return () => query.removeListener(handler)
    }

    return () => {}
  }

  customElements.define(
    'push-cart-multibuy',
    class PushCartMultibuy extends HTMLElement {
      connectedCallback () {
        this.onAfterAdd = this.onAfterAdd.bind(this)
        this.onClick = this.onClick.bind(this)
        this.onPushCartClick = this.onPushCartClick.bind(this)
        this.onKeydown = this.onKeydown.bind(this)
        this.onResize = this.onResize.bind(this)
        this.onDrawerChange = this.onDrawerChange.bind(this)
        this.onCarouselTouchStart = this.onCarouselTouchStart.bind(this)
        this.onCarouselTouchMove = this.onCarouselTouchMove.bind(this)
        this.onCarouselClickCapture = this.onCarouselClickCapture.bind(this)
        this.onCarouselMouseDown = this.onCarouselMouseDown.bind(this)
        this.onCarouselDragStart = this.onCarouselDragStart.bind(this)
        this.onCarouselMouseMove = this.onCarouselMouseMove.bind(this)
        this.onCarouselMouseUp = this.onCarouselMouseUp.bind(this)
        this.blockOutsidePickerInteraction =
          this.blockOutsidePickerInteraction.bind(this)
        this.preventPickerScroll = this.preventPickerScroll.bind(this)

        this.mobileQuery = window.matchMedia(MOBILE_QUERY)
        this._pushCart = this.getPushCart()
        this._drawer = this.closest('site-drawer')
        this._scrollLockOptions = { passive: false }
        this._unbindMobileQuery = bindMediaQuery(this.mobileQuery, this.onResize)

        window.BAO.eventBus.addEventListener(
          window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
          this.onAfterAdd
        )

        this.addEventListener('click', this.onClick)
        this.addEventListener('keydown', this.onKeydown)
        this._pushCart?.addEventListener('click', this.onPushCartClick)
        this._pushCart?.addEventListener('keydown', this.onKeydown)
        this._pushCart?.addEventListener(
          'click',
          this.blockOutsidePickerInteraction,
          true
        )
        this._pushCart?.addEventListener(
          'touchstart',
          this.blockOutsidePickerInteraction,
          true
        )

        this.initCarousel()

        if (this._drawer) {
          this._drawerObserver = new MutationObserver(this.onDrawerChange)
          this._drawerObserver.observe(this._drawer, {
            attributes: true,
            attributeFilter: ['class']
          })
        }
      }

      disconnectedCallback () {
        window.BAO.eventBus.removeEventListener(
          window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
          this.onAfterAdd
        )

        this.removeEventListener('click', this.onClick)
        this.removeEventListener('keydown', this.onKeydown)
        this._pushCart?.removeEventListener('click', this.onPushCartClick)
        this._pushCart?.removeEventListener('keydown', this.onKeydown)
        this._pushCart?.removeEventListener(
          'click',
          this.blockOutsidePickerInteraction,
          true
        )
        this._pushCart?.removeEventListener(
          'touchstart',
          this.blockOutsidePickerInteraction,
          true
        )
        this._unbindMobileQuery?.()
        this._unbindMobileQuery = null
        this.teardownCarousel()
        this._drawerObserver?.disconnect()
        this.setPickerLock(false)
        this.resetState()
      }

      onDrawerChange () {
        if (
          this._drawer &&
          !this._drawer.classList.contains('drw-Drawer-active')
        ) {
          this.resetState()
        }
      }

      onResize () {
        if (!this.isMobile() && this.isPickerOpen()) {
          this.closePicker({ immediate: true })
        }

        this.initCarousel()
      }

      onKeydown (event) {
        if (event.key === 'Escape' && this.isPickerOpen()) {
          event.preventDefault()
          this.closePicker()
        }
      }

      onAfterAdd (event) {
        const form = event.detail?.form
        const card =
          form?.closest('[data-multibuy-card]') ||
          this.activeCard

        if (!card) {
          return
        }

        if (this.isPickerOpen()) {
          this.closePicker({ immediate: true })
        } else {
          this.resetCard(card)
        }
      }

      onPushCartClick (event) {
        if (this.contains(event.target)) {
          return
        }

        this.onClick(event)
      }

      onClick (event) {
        const openPickerButton = event.target.closest('[data-multibuy-open-picker]')

        if (openPickerButton && this.contains(openPickerButton)) {
          event.preventDefault()

          if (!this.isMobile()) {
            return
          }

          const card = openPickerButton.closest('[data-multibuy-card]')

          if (card) {
            this.openPicker(card)
          }

          return
        }

        const addButton = event.target.closest('[data-multibuy-add]')

        if (addButton && this.contains(addButton)) {
          const card = addButton.closest('[data-multibuy-card]')

          if (!card) {
            return
          }

          const hasVariants = card.dataset.multibuyHasVariants === 'true'
          const isSizeSelected = card.classList.contains('psh-MultibuyCard--size-selected')

          if (hasVariants && !isSizeSelected) {
            event.preventDefault()

            if (!card.classList.contains('psh-MultibuyCard--selecting-size')) {
              card.classList.add('psh-MultibuyCard--selecting-size')

              const sizes = card.querySelector('[data-multibuy-sizes-desktop]')

              if (sizes) {
                sizes.hidden = false
              }
            }
          }

          return
        }

        const pickerDismiss = event.target.closest(
          '[data-multibuy-picker-backdrop], [data-multibuy-picker-close]'
        )

        if (pickerDismiss && this.isInPicker(pickerDismiss)) {
          event.preventDefault()
          this.closePicker()
          return
        }

        const pickerSizeButton = event.target.closest(
          '[data-multibuy-picker] [data-multibuy-size]'
        )

        if (pickerSizeButton && this.isInPicker(pickerSizeButton)) {
          event.preventDefault()
          this.selectPickerSize(pickerSizeButton)
          return
        }

        const cardSizeButton = event.target.closest('[data-multibuy-card] [data-multibuy-size]')

        if (cardSizeButton && this.contains(cardSizeButton) && !cardSizeButton.disabled) {
          event.preventDefault()
          this.selectCardSize(cardSizeButton)
          return
        }

        const pickerSubmit = event.target.closest('[data-multibuy-picker-submit]')

        if (pickerSubmit && this.isInPicker(pickerSubmit)) {
          event.preventDefault()
          this.submitPicker()
        }
      }

      resetState () {
        this.closePicker({ immediate: true })
        this.resetAllCards()
        this.resetCarouselScroll()
      }

      resetAllCards () {
        this.querySelectorAll('[data-multibuy-card]').forEach((card) => {
          this.resetCard(card)
        })
      }

      resetCarouselScroll () {
        const carousel =
          this._carousel || this.querySelector('.psh-Multibuy_Carousel')

        if (carousel) {
          carousel.scrollLeft = 0
        }
      }

      initCarousel () {
        this.teardownCarousel()

        this._carousel = this.querySelector('.psh-Multibuy_Carousel')
        this._carouselTrack = this.querySelector('.psh-Multibuy_CarouselTrack')

        if (!this._carousel || !this._carouselTrack) {
          return
        }

        const slides = this._carouselTrack.querySelectorAll('.psh-MultibuyCard')

        if (slides.length < 2) {
          return
        }

        this._carousel.addEventListener('touchstart', this.onCarouselTouchStart, {
          passive: true
        })
        this._carousel.addEventListener('touchmove', this.onCarouselTouchMove, {
          passive: true
        })
        this._carousel.addEventListener('click', this.onCarouselClickCapture, true)
        this._carousel.addEventListener('dragstart', this.onCarouselDragStart)

        if (!this.isMobile()) {
          this._carouselTrack.addEventListener('mousedown', this.onCarouselMouseDown)
        }
      }

      teardownCarousel () {
        if (this._carousel) {
          this._carousel.removeEventListener('touchstart', this.onCarouselTouchStart)
          this._carousel.removeEventListener('touchmove', this.onCarouselTouchMove)
          this._carousel.removeEventListener('click', this.onCarouselClickCapture, true)
          this._carousel.removeEventListener('dragstart', this.onCarouselDragStart)
        }

        if (this._carouselTrack) {
          this._carouselTrack.removeEventListener('mousedown', this.onCarouselMouseDown)
        }

        document.removeEventListener('mousemove', this.onCarouselMouseMove)
        document.removeEventListener('mouseup', this.onCarouselMouseUp)
        this.endCarouselDrag()
        this._carouselTouch = null
        this._suppressCarouselClick = false
        this._carousel = null
        this._carouselTrack = null
      }

      // Pressing on an image starts the browser's own "drag this picture" mode,
      // which swallows the mouse release and leaves our drag stuck on
      onCarouselDragStart (event) {
        event.preventDefault()
      }

      onCarouselMouseDown (event) {
        if (this.isMobile() || event.button !== 0) {
          return
        }

        this._carouselDrag = {
          startX: event.pageX,
          scrollLeft: this._carousel.scrollLeft,
          dragging: false
        }

        document.addEventListener('mousemove', this.onCarouselMouseMove)
        document.addEventListener('mouseup', this.onCarouselMouseUp)
      }

      onCarouselMouseMove (event) {
        if (!this._carouselDrag || !this._carousel) {
          return
        }

        // The button was released somewhere we didn't see (e.g. outside the window)
        if (event.buttons === 0) {
          this.onCarouselMouseUp()
          return
        }

        const distance = event.pageX - this._carouselDrag.startX

        if (!this._carouselDrag.dragging && Math.abs(distance) > 5) {
          this._carouselDrag.dragging = true
          this._carousel.classList.add('psh-Multibuy_Carousel--dragging')
        }

        if (!this._carouselDrag.dragging) {
          return
        }

        event.preventDefault()
        this._carousel.scrollLeft = this._carouselDrag.scrollLeft - distance
      }

      onCarouselMouseUp () {
        if (!this._carouselDrag) {
          return
        }

        if (this._carouselDrag.dragging) {
          this._suppressCarouselClick = true
        }

        this.endCarouselDrag()
      }

      endCarouselDrag () {
        document.removeEventListener('mousemove', this.onCarouselMouseMove)
        document.removeEventListener('mouseup', this.onCarouselMouseUp)
        this._carousel?.classList.remove('psh-Multibuy_Carousel--dragging')
        this._carouselDrag = null
      }

      onCarouselTouchStart (event) {
        const touch = event.touches[0]

        if (!touch) {
          return
        }

        this._carouselTouch = {
          x: touch.clientX,
          y: touch.clientY,
          scrolling: false
        }
      }

      onCarouselTouchMove (event) {
        const touch = event.touches[0]

        if (!this._carouselTouch || !touch) {
          return
        }

        const deltaX = Math.abs(touch.clientX - this._carouselTouch.x)
        const deltaY = Math.abs(touch.clientY - this._carouselTouch.y)

        if (deltaX > 8 && deltaX > deltaY) {
          this._carouselTouch.scrolling = true
        }
      }

      onCarouselClickCapture (event) {
        if (this._carouselTouch?.scrolling || this._suppressCarouselClick) {
          event.preventDefault()
          event.stopPropagation()
        }

        this._carouselTouch = null
        this._suppressCarouselClick = false
      }

      openPicker (card) {
        if (!this.isMobile()) {
          return
        }

        const picker = this.getPicker()
        const sizesSource = card.querySelector('[data-multibuy-sizes]')
        const sizesTarget = picker?.querySelector('[data-multibuy-picker-sizes]')
        const submitButton = picker?.querySelector('[data-multibuy-picker-submit]')

        if (!picker || !sizesSource || !sizesTarget) {
          return
        }

        this.cancelPickerClose()
        this.activeCard = card

        picker.classList.remove('psh-MultibuyPicker--visible')
        picker.hidden = false
        picker.setAttribute('aria-hidden', 'false')

        this.syncPickerContent(card, picker)

        sizesTarget.innerHTML = sizesSource.innerHTML
        this.clearSizeButtons(sizesTarget)

        if (submitButton) {
          submitButton.disabled = true
        }

        this.revealPicker(picker)
        this.setPickerLock(true)
      }

      revealPicker (picker) {
        if (this.prefersReducedMotion()) {
          picker.classList.add('psh-MultibuyPicker--visible')
          return
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            picker.classList.add('psh-MultibuyPicker--visible')
          })
        })
      }

      closePicker ({ immediate = false } = {}) {
        const picker = this.getPicker()

        if (!picker || picker.hidden) {
          return
        }

        this.cancelPickerClose()

        const finalizeClose = () => {
          this.cancelPickerClose()

          picker.classList.remove('psh-MultibuyPicker--visible')
          picker.hidden = true
          picker.setAttribute('aria-hidden', 'true')
          emptyElement(picker.querySelector('[data-multibuy-picker-sizes]'))

          if (this.activeCard) {
            this.resetCard(this.activeCard)
            this.activeCard = null
          }

          this.setPickerLock(false)
        }

        if (
          immediate ||
          this.prefersReducedMotion() ||
          !picker.classList.contains('psh-MultibuyPicker--visible')
        ) {
          finalizeClose()
          return
        }

        picker.classList.remove('psh-MultibuyPicker--visible')

        const panel = picker.querySelector('.psh-MultibuyPicker_Panel')

        if (!panel) {
          finalizeClose()
          return
        }

        const onTransitionEnd = (event) => {
          if (event.target !== panel || event.propertyName !== 'transform') {
            return
          }

          finalizeClose()
        }

        this._pickerClosePanel = panel
        this._pickerCloseHandler = onTransitionEnd
        panel.addEventListener('transitionend', onTransitionEnd)
        this._pickerCloseTimer = window.setTimeout(
          finalizeClose,
          PICKER_TRANSITION_MS + 50
        )
      }

      cancelPickerClose () {
        if (this._pickerCloseTimer) {
          window.clearTimeout(this._pickerCloseTimer)
          this._pickerCloseTimer = null
        }

        if (this._pickerClosePanel && this._pickerCloseHandler) {
          this._pickerClosePanel.removeEventListener(
            'transitionend',
            this._pickerCloseHandler
          )
        }

        this._pickerClosePanel = null
        this._pickerCloseHandler = null
      }

      blockOutsidePickerInteraction (event) {
        if (!this.isPickerOpen() || !this.isMobile()) {
          return
        }

        if (this.isInPicker(event.target)) {
          return
        }

        event.preventDefault()
        event.stopPropagation()
      }

      preventPickerScroll (event) {
        if (!this.isPickerOpen() || !this.isMobile()) {
          return
        }

        if (event.target.closest('[data-multibuy-picker-sizes]')) {
          return
        }

        event.preventDefault()
      }

      setPickerLock (enabled) {
        this._drawer?.classList.toggle('drw-Drawer--multibuy-picker-open', enabled)
        this._pushCart?.classList.toggle('psh-Cart--multibuy-picker-open', enabled)

        const scrollTargets = [this._drawer, this._pushCart].filter(Boolean)

        scrollTargets.forEach((target) => {
          target.removeEventListener('touchmove', this.preventPickerScroll)
          target.removeEventListener('wheel', this.preventPickerScroll)
        })

        if (!enabled) {
          return
        }

        scrollTargets.forEach((target) => {
          target.addEventListener('touchmove', this.preventPickerScroll, this._scrollLockOptions)
          target.addEventListener('wheel', this.preventPickerScroll, this._scrollLockOptions)
        })
      }

      syncPickerContent (card, picker) {
        const imageSource = card.querySelector('[data-multibuy-card-image]')
        const imageTarget = picker.querySelector('[data-multibuy-picker-image]')
        const titleTarget = picker.querySelector('[data-multibuy-picker-title]')
        const colourTarget = picker.querySelector('[data-multibuy-picker-colour]')
        const priceTarget = picker.querySelector('[data-multibuy-picker-price]')

        if (imageTarget && imageSource) {
          imageTarget.innerHTML = imageSource.innerHTML
        }

        if (titleTarget) {
          titleTarget.textContent = card.dataset.productTitle || ''
        }

        if (colourTarget) {
          const colour = card.dataset.productColour || ''

          if (colour) {
            colourTarget.textContent = colour
            colourTarget.hidden = false
          } else {
            colourTarget.textContent = ''
            colourTarget.hidden = true
          }
        }

        if (priceTarget) {
          const priceEl = card.querySelector('[data-multibuy-price]')
          priceTarget.textContent = priceEl?.textContent?.trim() || ''
        }

        this.setPickerStock(picker, card.dataset.productAvailable === 'true')
      }

      setPickerStock (picker, available) {
        const stock = picker?.querySelector('[data-multibuy-picker-stock]')
        const stockText = picker?.querySelector('[data-multibuy-picker-stock-text]')

        if (!stock || !stockText) {
          return
        }

        const isAvailable = available === true || available === 'true'

        stockText.textContent = isAvailable ? 'In stock' : 'Out of Stock'
        stock.classList.toggle('psh-MultibuyPicker_Stock--outOfStock', !isAvailable)
      }

      selectPickerSize (sizeButton) {
        if (sizeButton.disabled || !this.activeCard) {
          return
        }

        const picker = this.getPicker()
        const sizesTarget = picker?.querySelector('[data-multibuy-picker-sizes]')
        const submitButton = picker?.querySelector('[data-multibuy-picker-submit]')
        const priceTarget = picker?.querySelector('[data-multibuy-picker-price]')

        this.activateSizeButton(sizeButton, sizesTarget)
        this.applyVariantSelection(this.activeCard, sizeButton)

        if (priceTarget) {
          this.setPriceFromButton(priceTarget, sizeButton)
        }

        if (submitButton) {
          submitButton.disabled = false
        }

        this.setPickerStock(picker, sizeButton.dataset.variantAvailable === 'true')
      }

      selectCardSize (sizeButton) {
        const card = sizeButton.closest('[data-multibuy-card]')

        if (!card || this.isMobile()) {
          return
        }

        const form = card.querySelector('form[data-push-cart-form]')
        const variantInput = form?.querySelector('[data-multibuy-variant-input]')

        this.activateSizeButton(sizeButton, card)

        if (variantInput) {
          variantInput.value = sizeButton.dataset.variantId
        }

        if (!form || !variantInput?.value) {
          return
        }

        submitForm(form)
        this.resetCard(card)
      }

      submitPicker () {
        if (!this.activeCard) {
          return
        }

        const form = this.activeCard.querySelector('form[data-push-cart-form]')
        const variantInput = form?.querySelector('[data-multibuy-variant-input]')

        if (!form || !variantInput?.value) {
          return
        }

        submitForm(form)
      }

      resetCard (card) {
        const hasVariants = card.dataset.multibuyHasVariants === 'true'
        const variantInput = card.querySelector('[data-multibuy-variant-input]')
        const priceEl = card.querySelector('[data-multibuy-price]')
        const sizes = card.querySelector('[data-multibuy-sizes-desktop]')

        card.classList.remove(
          'psh-MultibuyCard--selecting-size',
          'psh-MultibuyCard--size-selected'
        )

        this.clearSizeButtons(card)

        if (sizes) {
          sizes.hidden = true
        }

        if (variantInput) {
          variantInput.value = hasVariants
            ? ''
            : card.dataset.multibuyDefaultVariantId || ''
        }

        if (priceEl) {
          this.restoreCardPrice(card, priceEl)
        }
      }

      clearSizeButtons (container) {
        container?.querySelectorAll('[data-multibuy-size]').forEach((button) => {
          button.classList.remove('psh-MultibuyCard_Size--selected')
          button.setAttribute('aria-pressed', 'false')
        })
      }

      activateSizeButton (button, container) {
        this.clearSizeButtons(container)
        button.classList.add('psh-MultibuyCard_Size--selected')
        button.setAttribute('aria-pressed', 'true')
      }

      applyVariantSelection (card, sizeButton) {
        const variantInput = card.querySelector('[data-multibuy-variant-input]')
        const priceEl = card.querySelector('[data-multibuy-price]')

        if (variantInput) {
          variantInput.value = sizeButton.dataset.variantId
        }

        if (priceEl) {
          this.setPriceFromButton(priceEl, sizeButton)
        }
      }

      restoreCardPrice (card, priceEl) {
        const display = card.dataset.multibuyOriginalPriceDisplay

        if (display) {
          priceEl.textContent = display
          return
        }

        if (card.dataset.multibuyOriginalPrice) {
          this.setPriceText(priceEl, card.dataset.multibuyOriginalPrice)
        }
      }

      setPriceFromButton (element, sizeButton) {
        const display = sizeButton.dataset.variantPriceDisplay

        if (display) {
          element.textContent = display
          return
        }

        if (sizeButton.dataset.variantPrice) {
          this.setPriceText(element, sizeButton.dataset.variantPrice)
        }
      }

      setPriceText (element, cents) {
        element.textContent = this.formatMoney(Number(cents))
      }

      isPickerOpen () {
        const picker = this.getPicker()
        return Boolean(picker && !picker.hidden)
      }

      isInPicker (element) {
        const picker = this.getPicker()
        return Boolean(picker && element && picker.contains(element))
      }

      isMobile () {
        return this.mobileQuery?.matches ?? window.innerWidth <= 767
      }

      prefersReducedMotion () {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      }

      getPicker () {
        return this.getPushCart()?.querySelector('[data-multibuy-picker]')
      }

      getPushCart () {
        return this.closest('push-cart')
      }

      formatMoney (cents) {
        if (window.Shopify?.formatMoney) {
          return window.Shopify.formatMoney(cents)
        }

        return (cents / 100).toFixed(2)
      }
    }
  )
}
